import { useState } from 'react';
import { Shield, MapPin, BarChart3, Mail, ChevronUp, ChevronDown } from 'lucide-react';
import { useGDPR } from '../../context/GDPRContext';
import toast from 'react-hot-toast';

export function GDPRConsentModal() {
  const { showGDPRModal, acceptGDPR, loading: gdprLoading } = useGDPR();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [settings, setSettings] = useState({
    locationConsent: true,
    marketingConsent: true,
    analyticsConsent: true
  });

  // Don't show modal if GDPR context is loading or modal shouldn't be shown
  if (!showGDPRModal || gdprLoading) return null;

  const handleAcceptSelected = async () => {
    try {
      setLoading(true);
      await acceptGDPR({
        gdprAccepted: true,
        ...settings
      });
      toast.success('Privacy preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleEssentialOnly = async () => {
    try {
      setLoading(true);
      await acceptGDPR({
        gdprAccepted: true,
        locationConsent: false,
        marketingConsent: false,
        analyticsConsent: false
      });
      toast.success('Privacy preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-end justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl w-full max-w-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Collapsed View */}
        {!isExpanded ? (
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  By continuing, you agree to our{' '}
                  <a href="/creators-hub?tab=legal&legal=terms" className="text-[#728c00] hover:underline">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/creators-hub?tab=legal&legal=privacy" className="text-[#728c00] hover:underline">
                    Privacy Policy
                  </a>
                  . You can change these preferences anytime in your account settings.
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleEssentialOnly}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-[#065900] dark:text-[#065900] bg-white dark:bg-gray-800 border border-[#065900] hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Essential Only
                  </button>
                  
                  <button
                    onClick={handleAcceptSelected}
                    disabled={loading}
                    className="px-6 py-2 text-sm font-medium text-white bg-[#065900] hover:bg-[#054700] rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Accept Selected'}
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setIsExpanded(true)}
                className="ml-4 p-2 text-gray-500 hover:text-[#728c00] transition-colors"
                aria-label="Expand privacy options"
              >
                <ChevronUp className="w-6 h-6" />
              </button>
            </div>
          </div>
        ) : (
          /* Expanded View */
          <div className="max-h-[80vh] overflow-y-auto scrollbar-hide">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8 text-[#728c00]" />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Privacy Preferences
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Choose how we can use your data to improve your experience
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 text-gray-500 hover:text-[#728c00] transition-colors"
                  aria-label="Collapse privacy options"
                >
                  <ChevronDown className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Location Consent */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <MapPin className="w-6 h-6 text-[#728c00] mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                          Location Services
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          Allow us to access your location to provide personalized content, 
                          local recommendations, and improve our services.
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Used for: Local content, event recommendations, nearby connections
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSetting('locationConsent')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#728c00] focus:ring-offset-2 ${
                        settings.locationConsent ? 'bg-[#728c00]' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.locationConsent ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Analytics Consent */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <BarChart3 className="w-6 h-6 text-[#728c00] mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                          Analytics & Performance
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          Help us understand how you use our platform to improve 
                          performance and user experience.
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Used for: Usage analytics, performance monitoring, feature optimization
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSetting('analyticsConsent')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#728c00] focus:ring-offset-2 ${
                        settings.analyticsConsent ? 'bg-[#728c00]' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.analyticsConsent ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Marketing Consent */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <Mail className="w-6 h-6 text-[#065900] mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                          Marketing & Communications
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          Receive personalized recommendations, updates about new features, 
                          and promotional content tailored to your interests.
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Used for: Email newsletters, push notifications, personalized ads
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSetting('marketingConsent')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#728c00] focus:ring-offset-2 ${
                        settings.marketingConsent ? 'bg-[#728c00]' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.marketingConsent ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleEssentialOnly}
                    disabled={loading}
                    className="flex-1 px-6 py-3 text-sm font-medium text-[#065900] dark:text-[#065900] bg-white dark:bg-gray-800 border border-[#065900] hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Essential Only
                  </button>
                  
                  <button
                    onClick={handleAcceptSelected}
                    disabled={loading}
                    className="flex-1 px-6 py-3 text-sm font-medium text-white bg-[#065900] hover:bg-[#054700] rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Accept Selected'}
                  </button>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
                  You can change these preferences anytime in your account settings
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
